import React, { createContext, useState, useContext, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  User,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  sendEmailVerification,
  browserPopupRedirectResolver
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import { fastApiClient, type UserInfo } from "@/api/fastApiClient";
import { AppShellSkeleton } from "@/components/layout/AppShellSkeleton";
import { linkSignedInBrowser, linkSignupBrowser } from "@/lib/analytics";
import {
  EMAIL_VERIFICATION_REQUIRED_MESSAGE,
  emailVerificationActionSettings,
  requiresEmailVerification,
} from "@/lib/emailVerification";

interface AuthContextType {
  currentUser: User | null;
  userInfo: UserInfo | null;
  isAdmin: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUserInfo: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastVerifiedUid, setLastVerifiedUid] = useState<string | null>(null);

  // The marketing home page needs no auth state, so it renders immediately
  // (and matches the prerendered HTML) instead of waiting behind the skeleton.
  const [renderBeforeAuth] = useState(() => window.location.pathname === "/");

  async function verifyAndGetUserInfo(user: User) {
    try {
      const info = await fastApiClient.verifyAuth();
      setUserInfo(info);
      setLastVerifiedUid(user.uid);
    } catch (err) {
      console.error("Failed to verify user with backend:", err);
      setUserInfo(null);
      setLastVerifiedUid(user.uid);
    }
  }

  async function refreshUserInfo() {
    if (currentUser) {
      try {
        const info = await fastApiClient.getCurrentUser();
        setUserInfo(info);
      } catch (err) {
        console.error("Failed to refresh user info:", err);
      }
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // An unverified password account must not resume from a stale token —
      // otherwise the gate only holds on the login call itself.
      if (requiresEmailVerification(user)) {
        setCurrentUser(null);
        setUserInfo(null);
        setLastVerifiedUid(null);
        try {
          localStorage.removeItem("hoa:isLoggedIn");
        } catch {
          /* private mode / blocked storage */
        }
        setLoading(false);
        return;
      }
      setCurrentUser(user);
      // Lightweight marker for the Astro SEO pages, which are served outside the
      // SPA and so cannot read AuthProvider. Lets them show the right CTA to a
      // signed-in visitor. Not an auth signal — nothing is gated on it.
      try {
        if (user) localStorage.setItem("hoa:isLoggedIn", "1");
        else localStorage.removeItem("hoa:isLoggedIn");
      } catch {
        /* private mode / blocked storage */
      }
      // Ties this browser to the signed-in account for "How they found us"
      // (and resends a signup link that failed earlier). Fire-and-forget.
      if (user) void linkSignedInBrowser(user.uid);
      if (!user) {
        setUserInfo(null);
        setLastVerifiedUid(null);
      } else if (user.uid !== lastVerifiedUid) {
        await verifyAndGetUserInfo(user);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [lastVerifiedUid]);

  async function signup(email: string, password: string) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    // Send the link, then sign straight back out: an account is not usable
    // until the address is confirmed, so leaving the session live would let a
    // mistyped address straight into the product.
    try {
      await sendEmailVerification(userCredential.user, emailVerificationActionSettings());
    } finally {
      // The new user is signed out below, so this is the only moment the
      // browser they arrived in can be tied to the account. Best effort:
      // never throws, waits at most ~2 s, and never changes the signup result.
      await linkSignupBrowser(userCredential.user).catch(() => undefined);
      await signOut(auth);
      setCurrentUser(null);
      setUserInfo(null);
      setLastVerifiedUid(null);
    }
  }

  async function login(email: string, password: string) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    // reload() so a link clicked since the last session is picked up rather
    // than trusting the cached emailVerified flag.
    await userCredential.user.reload();
    const verifiedUser = auth.currentUser || userCredential.user;
    if (requiresEmailVerification(verifiedUser)) {
      await sendEmailVerification(verifiedUser, emailVerificationActionSettings()).catch(() => undefined);
      await signOut(auth);
      setCurrentUser(null);
      setUserInfo(null);
      setLastVerifiedUid(null);
      throw new Error(EMAIL_VERIFICATION_REQUIRED_MESSAGE);
    }
    await verifyAndGetUserInfo(verifiedUser);
  }

  async function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
    await verifyAndGetUserInfo(userCredential.user);
  }

  async function logout() {
    await signOut(auth);
  }

  async function resetPassword(email: string) {
    await sendPasswordResetEmail(auth, email);
  }

  const isAdmin = Boolean(userInfo?.is_admin);

  const value = {
    currentUser,
    userInfo,
    isAdmin,
    loading,
    login,
    signup,
    signInWithGoogle,
    logout,
    resetPassword,
    refreshUserInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading && !renderBeforeAuth ? <AppShellSkeleton /> : children}
    </AuthContext.Provider>
  );
}
